import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiLoader, FiPlay, FiPause, FiDownload, FiRefreshCw, FiBox, FiSquare } from "react-icons/fi";
import { Studio, CAPTION_STYLES, DEFAULT_LOOK, loadAudioBuffer, recordVideo } from "../../video/studio";
import { SIZES, recordableFormats } from "../../video/timeline";
import { analyseSpeech } from "../../video/lipsync";
import { buildToonScenes } from "../../video/toon/story";
import { loadSolid } from "../../video/toon/render";
import { KINDS } from "../../video/toon/cast";
import { Pills, card, inputClass, labelClass, saveBlob, slug, fmt, PREVIEW } from "../VideoStudioPanel";

// The studio of a finished cartoon: play it in the flat or the solid look, then save it as a video file.
export default function ToonStudio({ view, segments, mode: firstMode = "2d" }) {
  const canvasRef = useRef(null);
  const studioRef = useRef(null);
  const audioRef = useRef({ context: null, stop: null });
  const cancelRef = useRef({ cancelled: false });
  const timeRef = useRef(0);
  const playingRef = useRef(false);
  const clock = useRef({ start: 0, from: 0 });
  const [scenes, setScenes] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [mode, setMode] = useState(firstMode);
  const [solidOk, setSolidOk] = useState(null);                      // null = not tried yet
  const [look, setLook] = useState({ ...DEFAULT_LOOK, grade: "none", vignette: 0.12, grain: 0, captions: "subtitles", title: segments.title || view.original_name || "", watermark: "", letterbox: false });
  const [time, setTimeState] = useState(0);
  const [total, setTotal] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [resolution, setResolution] = useState("720p");
  const [formats] = useState(() => recordableFormats());
  const [format, setFormat] = useState(0);
  const [rendering, setRendering] = useState(false);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState("");
  const ratio = segments.ratio || "16:9";
  const setTime = (t) => { timeRef.current = t; setTimeState(t); };

  // the recorded voices, and what the lips do to each of them
  useEffect(() => {
    let alive = true;
    const context = new (window.AudioContext || window.webkitAudioContext)();
    const audio = audioRef.current;
    audio.context = context;
    (async () => {
      try {
        const beats = segments.scenes || [];
        const sounds = await Promise.all(beats.map(async (b) => {
          const audioBuffer = b.audio ? await loadAudioBuffer(context, b.audio).catch(() => null) : null;
          return { audioBuffer, analysis: audioBuffer ? analyseSpeech(audioBuffer) : null };
        }));
        if (!alive) return;
        const wanted = firstMode === "3d" && (await loadSolid()) ? "3d" : "2d";
        if (alive) { setSolidOk(wanted === "3d" ? true : null); setMode(wanted); setScenes(buildToonScenes(segments, wanted, sounds)); }
      } catch {
        if (alive) setLoadError("The voices could not be loaded into the studio. Reload the page and open the video again.");
      }
    })();
    return () => { alive = false; cancelRef.current.cancelled = true; if (audio.stop) audio.stop(); context.close().catch(() => {}); };
  }, [segments]); // eslint-disable-line react-hooks/exhaustive-deps -- loads once per video

  useEffect(() => {
    if (!scenes || !canvasRef.current) return;
    if (!studioRef.current) studioRef.current = new Studio(canvasRef.current, scenes, look, PREVIEW[ratio] || PREVIEW["16:9"]);
    else studioRef.current.setScenes(scenes);
    studioRef.current.setLook(look);
    studioRef.current.drawFrame(timeRef.current, playingRef.current);
    setTotal(studioRef.current.total);
  }, [scenes, look, ratio]);

  const stopAudio = () => { if (audioRef.current.stop) { audioRef.current.stop(); audioRef.current.stop = null; } };
  const play = useCallback(async (from) => {
    const studio = studioRef.current;
    if (!studio) return;
    const { context } = audioRef.current;
    await context.resume();
    stopAudio();
    audioRef.current.stop = studio.playAudio(context, context.destination, from, null);
    clock.current = { start: performance.now(), from };
    playingRef.current = true;
    setPlaying(true);
  }, []);
  const pause = useCallback(() => {
    stopAudio();
    playingRef.current = false;
    studioRef.current?.drawFrame(timeRef.current, false);
    setPlaying(false);
  }, []);

  useEffect(() => {
    if (!playing) return undefined;
    const id = setInterval(() => {
      const studio = studioRef.current;
      const t = clock.current.from + (performance.now() - clock.current.start) / 1000;
      if (t >= studio.total) { setTime(studio.total); stopAudio(); playingRef.current = false; studio.drawFrame(studio.total, false); setPlaying(false); return; }
      studio.drawFrame(t, true);
      setTime(t);
    }, 33);
    return () => clearInterval(id);
  }, [playing]);

  const seek = (t) => { setTime(t); studioRef.current?.drawFrame(t, false); if (playing) play(t); };
  const setL = (patch) => setLook((l) => ({ ...l, ...patch }));

  const choose = async (next) => {
    setMessage("");
    if (next === "3d" && !(await loadSolid())) { setSolidOk(false); setMessage("This browser cannot draw in 3D (WebGL is off). The flat 2D look is used."); return; }
    setSolidOk(next === "3d" ? true : solidOk);
    setMode(next);
    setScenes((list) => list.map((s) => ({ ...s, toon: { ...s.toon, mode: next } })));
  };

  const render = async () => {
    if (!formats.length) { setMessage("This browser cannot save videos. Use a current Chrome, Edge, Firefox or Safari."); return; }
    setMessage("");
    setRendering(true);
    setProgress(0);
    pause();
    cancelRef.current = { cancelled: false };
    try {
      const canvas = document.createElement("canvas");
      const studio = new Studio(canvas, scenes, look, SIZES[ratio][resolution]);
      const blob = await recordVideo(studio, { mime: formats[format].mime, bitrate: resolution === "1080p" ? 9e6 : 5e6, onProgress: setProgress, signal: cancelRef.current });
      saveBlob(`${slug(look.title || view.original_name)}.${formats[format].ext}`, blob);
      setMessage(`Saved ${(blob.size / 1048576).toFixed(1)} MB.`);
    } catch (e) {
      setMessage(e.message === "cancelled" ? "Stopped." : `The video could not be saved (${e.message || "unknown error"}).`);
    }
    studioRef.current?.drawFrame(timeRef.current, false);
    setRendering(false);
  };

  if (loadError) return <div className={`${card} mt-6 text-sm text-red-200`}>{loadError}</div>;
  if (!scenes) return <div className={`${card} mt-6 flex items-center gap-3 text-sm text-gray-300`}><FiLoader className="animate-spin" /> Opening the studio...</div>;

  const voiceless = !scenes.some((s) => s.audioBuffer);
  return (
    <div className={`${card} mt-6 border-accent-light/30`} data-testid="toon-studio">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0"><p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-1">Cartoon studio</p><p className="font-semibold" dir="auto">{view.original_name}</p></div>
        <div className="flex flex-wrap gap-2">
          {(segments.cast || []).map((c) => <span key={c.id} className="px-2.5 py-1 rounded-full text-xs bg-white/10 text-gray-200" title={`${KINDS[c.kind]?.label || c.kind} · ${c.voice || ""}`}>{c.name}</span>)}
        </div>
      </div>
      {segments.notice && <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mb-4">{segments.notice}</p>}

      <div className="grid lg:grid-cols-[1.3fr_1fr] gap-6 items-start">
        <div className="lg:sticky lg:top-24">
          <div className="rounded-xl overflow-hidden bg-black flex justify-center">
            <canvas ref={canvasRef} className={`block max-w-full ${ratio === "9:16" ? "max-h-[600px] w-auto" : "w-full"}`} style={{ aspectRatio: ratio.replace(":", " / ") }} aria-label="Cartoon preview" />
          </div>
          <div className="flex items-center gap-3 mt-3">
            <button type="button" onClick={() => (playing ? pause() : play(time >= total - 0.05 ? 0 : time))} className="w-11 h-11 rounded-full bg-brand-gradient text-white flex items-center justify-center shrink-0" aria-label={playing ? "Pause" : "Play"}>{playing ? <FiPause /> : <FiPlay />}</button>
            <input type="range" min="0" max={Math.max(0.1, total)} step="0.05" value={Math.min(time, total)} onChange={(e) => seek(Number(e.target.value))} className="flex-1 accent-[#22d3ee]" aria-label="Position in the video" />
            <span className="text-xs text-gray-400 tabular-nums w-24 text-end">{fmt(time)} / {fmt(total)}</span>
          </div>
          {voiceless && <p className="text-xs text-gray-500 mt-2">None of the voices could be loaded, so the video plays without sound.</p>}
        </div>

        <div className="space-y-5">
          <div>
            <span className={labelClass}>Look</span>
            <div className="flex gap-2" role="group" aria-label="Look of the cartoon">
              {[["2d", "Flat 2D cartoon", FiSquare], ["3d", "Solid 3D cartoon", FiBox]].map(([key, label, Icon]) => (
                <button key={key} type="button" onClick={() => choose(key)} aria-pressed={mode === key} disabled={key === "3d" && solidOk === false}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold border transition-colors disabled:opacity-50 ${mode === key ? "bg-accent-light text-navy border-accent-light" : "bg-white/5 border-navy-border text-gray-300 hover:border-accent-light"}`}><Icon size={15} /> {label}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label htmlFor="t-title" className={labelClass}>Title card</label><input id="t-title" className={inputClass} value={look.title} maxLength={80} onChange={(e) => setL({ title: e.target.value })} dir="auto" placeholder="(none)" /></div>
            <div><label htmlFor="t-outro" className={labelClass}>Closing card</label><input id="t-outro" className={inputClass} value={look.outro} maxLength={80} onChange={(e) => setL({ outro: e.target.value })} dir="auto" placeholder="e.g. The End" /></div>
          </div>
          <div><span className={labelClass}>Subtitles</span><Pills label="Subtitles" value={look.captions} onChange={(captions) => setL({ captions })} options={Object.entries(CAPTION_STYLES)} /></div>
          <div><label htmlFor="t-mark" className={labelClass}>Watermark text</label><input id="t-mark" className={inputClass} value={look.watermark} maxLength={40} onChange={(e) => setL({ watermark: e.target.value })} placeholder="e.g. @yourbrand" dir="auto" /></div>
        </div>
      </div>

      <div className="mt-6 border-t border-navy-border pt-5 flex flex-wrap items-end gap-4">
        <div><span className={labelClass}>Quality</span><Pills label="Quality" value={resolution} onChange={setResolution} options={[["720p", "720p HD"], ["1080p", "1080p Full HD"]]} /></div>
        {formats.length > 1 && <div><span className={labelClass}>File type</span><Pills label="File type" value={String(format)} onChange={(v) => setFormat(Number(v))} options={formats.map((f, i) => [String(i), f.label])} /></div>}
        <button type="button" onClick={rendering ? () => { cancelRef.current.cancelled = true; } : render} disabled={!formats.length}
          className="ms-auto flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brand-gradient text-white font-semibold disabled:opacity-50">
          {rendering ? <><FiLoader className="animate-spin" /> Recording {Math.round(progress * 100)}% - stop</> : <><FiDownload /> Save the video ({fmt(total)})</>}
        </button>
      </div>
      <p className="text-xs text-gray-500 mt-3">The video is put together in your browser, in real time (a {fmt(total)} video takes about {fmt(total + 1)}) - keep this tab open while it records. {formats.length ? "" : "This browser cannot record videos."}</p>
      {message && <p className="text-sm text-amber-200 mt-3 inline-flex items-center gap-2"><FiRefreshCw size={13} /> {message}</p>}
    </div>
  );
}
